// src/context/SearchContext.jsx
import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";

const SearchContext = createContext(null);
export const STEPS = ["Type", "Trip details"];
const DEFAULT_CATEGORIES = ["normal", "premium"];

const normalizeCategoryList = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || "").trim()).filter(Boolean);
  }
  if (!value) return [];
  return String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const deriveCategoryValue = (list) => {
  const clean = normalizeCategoryList(list);
  if (clean.length > 1) return "all";
  return clean[0] ?? null;
};

/* Helpers */
const toLocalDateTime = (dateStr, time24) => {
  if (!dateStr || !time24) return null;
  const [y, m, d] = dateStr.split("-").map(Number);
  const [hh, mm] = time24.split(":").map(Number);
  const dt = new Date();
  dt.setFullYear(y, m - 1, d);
  dt.setHours(hh, mm || 0, 0, 0);
  return dt;
};
const hoursDiff = (a, b) =>
  a && b ? (b.getTime() - a.getTime()) / 3600000 : null;

/** Single source of truth for a clean payload */
const INITIAL_PAYLOAD = {
  pickupLocation: "",
  pickupDate: "",
  pickupTime24: "",
  destinations: [],
  stopCoordinates: [],
  finalDestination: "",
  releaseDate: "",
  releaseTime24: "",
  radius: 20,

  totalDistance: null,
  durationHours: null,
  minDropOffsetMin: null,

  pickupResolved: "",
  finalResolved: "",
  stopsResolved: [],
  pickupLat: null,
  pickupLng: null,
  dropLat: null,
  dropLng: null,

  roundTrip: false,

  // legacy mirrors
  carType: null,
  category: "all",
  categories: DEFAULT_CATEGORIES,
};

export function SearchProvider({ children }) {
  const navigate = useNavigate();
  const { search } = useLocation();

  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [types, setTypes] = useState([]);

  const category = deriveCategoryValue(categories);
  const type = types[0] ?? null;

  const [payload, setPayload] = useState(INITIAL_PAYLOAD);

  useEffect(() => {
    const params = new URLSearchParams(search);
    const hasParams = Array.from(params.keys()).length > 0;

    if (!hasParams) {
      setCategories(DEFAULT_CATEGORIES);
      setTypes([]);
      setPayload({
        ...INITIAL_PAYLOAD,
        category: "all",
        categories: DEFAULT_CATEGORIES,
      });
      return;
    }

    const p = Object.fromEntries(params.entries());
    const readListCSV = (val) =>
      !val ? [] : String(val).split(",").map((s) => s.trim()).filter(Boolean);

    const nextCategories =
      p.categories
        ? normalizeCategoryList(p.categories)
        : p.category === "all"
        ? DEFAULT_CATEGORIES
        : p.category
        ? normalizeCategoryList(p.category)
        : DEFAULT_CATEGORIES;

    const nextTypes =
      p.types ? readListCSV(p.types) :
      p.carType ? readListCSV(p.carType) :
      p.type ? readListCSV(p.type) :
      [];

    setCategories(nextCategories.length ? nextCategories : DEFAULT_CATEGORIES);
    setTypes(nextTypes);

    const destinations = params.getAll("destinations");
    const stopsResolved = params.getAll("stopsResolved");
    const stopCoordinates = params.getAll("stopCoordinates");

    setPayload({
      pickupLocation: p.pickupLocation ?? "",
      pickupDate: p.pickupDate ?? "",
      pickupTime24: p.pickupTime24 ?? "",
      destinations,
      stopCoordinates,
      finalDestination: p.finalDestination ?? "",
      releaseDate: p.releaseDate ?? "",
      releaseTime24: p.releaseTime24 ?? "",
      radius: p.radius ? Number(p.radius) : INITIAL_PAYLOAD.radius,

      totalDistance: p.totalDistance ? Number(p.totalDistance) : null,
      durationHours: p.durationHours ? Number(p.durationHours) : null,
      minDropOffsetMin: p.minDropOffsetMin ? Number(p.minDropOffsetMin) : null,

      pickupResolved: p.pickupResolved ?? "",
      finalResolved: p.finalResolved ?? "",
      stopsResolved,
      pickupLat: p.pickupLat ? Number(p.pickupLat) : null,
      pickupLng: p.pickupLng ? Number(p.pickupLng) : null,
      dropLat: p.dropLat ? Number(p.dropLat) : null,
      dropLng: p.dropLng ? Number(p.dropLng) : null,

      roundTrip:
        p.roundTrip === "true" ? true :
        p.roundTrip === "false" ? false : false,

      carType: nextTypes[0] ?? null,
      category: deriveCategoryValue(nextCategories.length ? nextCategories : DEFAULT_CATEGORIES),
      categories: nextCategories.length ? nextCategories : DEFAULT_CATEGORIES,
    });
  }, [search]);

  const step = useMemo(() => {
    if (!types.length) return 0;
    return 1;
  }, [types.length]);

  const toggleInArray = (arr, val) =>
    arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val];

  const toggleCategory = useCallback(() => {
    setCategories(DEFAULT_CATEGORIES);
    setPayload((p) => ({ ...p, categories: DEFAULT_CATEGORIES, category: "all" }));
  }, []);

  const toggleType = useCallback((label) => {
    setTypes((prev) => {
      const next = toggleInArray(prev, label);
      setPayload((p) => ({
        ...p,
        carType: next[0] ?? null,
        categories,
        category: deriveCategoryValue(categories),
      }));
      return next;
    });
  }, [categories]);

  const setCategory = useCallback(() => {
    setCategories(DEFAULT_CATEGORIES);
    setPayload((p) => ({ ...p, categories: DEFAULT_CATEGORIES, category: "all" }));
  }, []);

  const setType = useCallback((single) => {
    setTypes(single ? [single] : []);
    setPayload((p) => ({
      ...p,
      carType: single ?? null,
      categories,
      category: deriveCategoryValue(categories),
    }));
  }, [categories]);

  const clearSelections = useCallback(() => {
    setCategories(DEFAULT_CATEGORIES);
    setTypes([]);
    setPayload((p) => ({
      ...p,
      categories: DEFAULT_CATEGORIES,
      category: "all",
      carType: null,
    }));
  }, []);

  const clearAll = useCallback(() => {
    setCategories(DEFAULT_CATEGORIES);
    setTypes([]);
    setPayload({
      ...INITIAL_PAYLOAD,
      category: "all",
      categories: DEFAULT_CATEGORIES,
    });
  }, []);

  const getActualDurationHours = useCallback((p) => {
    const start = toLocalDateTime(p.pickupDate, p.pickupTime24);
    const end = toLocalDateTime(p.releaseDate, p.releaseTime24);
    const h = hoursDiff(start, end);
    if (h == null) return null;
    return Math.max(0, Math.round(h * 100) / 100);
  }, []);

  const pushToResults = (overridePayload = null) => {
    const nextPayload = overridePayload ? { ...payload, ...overridePayload } : payload;
    const params = new URLSearchParams();
    const resolvedStops = Array.isArray(nextPayload.stopsResolved)
      ? nextPayload.stopsResolved.map((s) => String(s || "").trim()).filter(Boolean)
      : [];
    const stopCoordinates = Array.isArray(nextPayload.stopCoordinates)
      ? nextPayload.stopCoordinates.map((s) => String(s || "").trim())
      : [];
    const effectiveCategories = normalizeCategoryList(
      nextPayload.categories?.length ? nextPayload.categories : categories
    );
    const effectiveCategory = deriveCategoryValue(effectiveCategories);
    if (types.length) params.set("types", types.join(","));
    if (type) params.set("type", type);
    if (effectiveCategories.length) params.set("categories", effectiveCategories.join(","));
    if (effectiveCategory) params.set("category", effectiveCategory);

    const actualHrs = getActualDurationHours(nextPayload);
    const requiredHrs = Number(nextPayload.durationHours) || 0;
    const durationToSend =
      actualHrs == null ? (requiredHrs || null) : Math.max(actualHrs, requiredHrs);

    const primitives = {
      pickupLocation: nextPayload.pickupLocation,
      pickupDate: nextPayload.pickupDate,
      pickupTime24: nextPayload.pickupTime24,
      finalDestination: nextPayload.finalDestination,
      releaseDate: nextPayload.releaseDate,
      releaseTime24: nextPayload.releaseTime24,
      radius: nextPayload.radius,
      totalDistance: nextPayload.totalDistance,
      pickupResolved: nextPayload.pickupResolved,
      finalResolved: nextPayload.finalResolved,
      pickupLat: nextPayload.pickupLat,
      pickupLng: nextPayload.pickupLng,
      dropLat: nextPayload.dropLat,
      dropLng: nextPayload.dropLng,
      roundTrip: String(Boolean(nextPayload.roundTrip)),
      carType: nextPayload.carType,
      category: effectiveCategory,
      durationHours: durationToSend,
      minDropOffsetMin: nextPayload.minDropOffsetMin,
    };

    Object.entries(primitives).forEach(([k, v]) => {
      if (v === null || v === undefined || v === "") return;
      params.set(k, String(v));
    });

    resolvedStops.forEach((s) => {
      if (s != null && String(s).trim()) params.append("destinations", String(s));
    });

    resolvedStops.forEach((s) => {
      if (s != null && String(s).trim()) params.append("stopsResolved", String(s));
    });

    stopCoordinates.forEach((s, idx) => {
      const hasResolvedStop = Boolean(resolvedStops[idx]);
      if (hasResolvedStop && s) params.append("stopCoordinates", s);
    });

    navigate(`/results?${params.toString()}`);
  };

  const value = useMemo(
    () => ({
      categories,
      types,
      category,
      type,
      payload,
      setPayload,
      step,
      toggleCategory,
      toggleType,
      setCategory,
      setType,
      clearSelections,
      clearAll,
      pushToResults,
    }),
    [
      categories,
      types,
      category,
      type,
      payload,
      step,
      toggleCategory,
      toggleType,
      setCategory,
      setType,
      clearSelections,
      clearAll,
    ]
  );

  return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>;
}

export function useSearch() {
  const ctx = useContext(SearchContext);
  if (!ctx) throw new Error("useSearch must be used inside <SearchProvider>");
  return ctx;
} 
